/**
 * Partner Quote Widget
 *
 * Component for fetching and displaying production cost quotes from partner APIs
 * (Scalable Press, Apliiq, Dimona)
 *
 * Usage:
 * <PartnerQuoteWidget
 *   garmentSKU="GILDAN-5000"
 *   quantity={50}
 *   decorationSpecs={{
 *     method: 'dtg',
 *     placements: [{
 *       location: 'front_chest',
 *       width: 12,
 *       height: 12,
 *       artworkUrl: 'https://...'
 *     }]
 *   }}
 *   garmentDetails={{
 *     type: 'tshirt',
 *     color: 'white',
 *     sizes: ['S', 'M', 'L', 'XL']
 *   }}
 *   onQuoteSelected={(quote) => console.log('Selected:', quote)}
 * />
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Divider,
  Grid,
  Tooltip,
  IconButton,
  Collapse,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  CompareArrows as CompareArrowsIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import axios from 'axios';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';

const PartnerQuoteWidget = ({
  garmentSKU,
  quantity,
  decorationSpecs,
  garmentDetails,
  onQuoteSelected,
  onSave,
  saving = false,
  initialQuotes = [],
  autoFetch = false,
}) => {
  const [quotes, setQuotes] = useState(initialQuotes);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [expandedQuotes, setExpandedQuotes] = useState(new Set());

  // Auto-fetch quotes on mount if enabled
  useEffect(() => {
    if (autoFetch && quotes.length === 0) {
      fetchQuotes();
    }
  }, [autoFetch]);

  const fetchQuotes = async () => {
    // Validate required fields
    if (!garmentSKU || !quantity || !decorationSpecs) {
      toast.error('Missing required quote parameters');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post('/api/partner-quotes/compare', {
        garmentSKU,
        quantity,
        decorationSpecs,
        garmentDetails,
      });

      if (response.data.success && response.data.quotes) {
        setQuotes(response.data.quotes);

        // Auto-select recommended quote
        const recommended = response.data.quotes.find(q => q.recommended);
        if (recommended) {
          setSelectedQuote(recommended);
          if (onQuoteSelected) {
            onQuoteSelected(recommended);
          }
        }

        toast.success(`Received ${response.data.quotes.length} quotes from partners`);
      } else {
        throw new Error('No quotes received');
      }
    } catch (err) {
      console.error('Error fetching quotes:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch quotes');
      toast.error('Failed to get partner quotes');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectQuote = (quote) => {
    setSelectedQuote(quote);
    if (onQuoteSelected) {
      onQuoteSelected(quote);
    }
    toast.success(`Selected quote from ${quote.partnerName}`);
  };

  const toggleExpanded = (partnerId) => {
    setExpandedQuotes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(partnerId)) {
        newSet.delete(partnerId);
      } else {
        newSet.add(partnerId);
      }
      return newSet;
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getIntegrationClassColor = (integrationClass) => {
    switch (integrationClass) {
      case 'A':
        return 'success';
      case 'B':
        return 'primary';
      case 'C':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      <Card>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Production Partner Quotes
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
                onClick={fetchQuotes}
                disabled={loading}
                variant="outlined"
                size="small"
              >
                {quotes.length === 0 ? 'Get Quotes' : 'Refresh'}
              </Button>
              {onSave && selectedQuote && quotes.length > 0 && (
                <Button
                  onClick={() => onSave(quotes, selectedQuote)}
                  disabled={saving || !selectedQuote}
                  variant="contained"
                  size="small"
                  startIcon={saving ? <CircularProgress size={16} /> : <CheckCircleIcon />}
                >
                  {saving ? 'Saving...' : 'Save Quote'}
                </Button>
              )}
            </Stack>
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading && quotes.length === 0 && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          )}

          {quotes.length > 0 && (
            <Stack spacing={2}>
              {quotes.map((quote) => {
                const isExpanded = expandedQuotes.has(quote.partnerId);
                const isSelected = selectedQuote?.partnerId === quote.partnerId;

                return (
                  <Card
                    key={quote.partnerId}
                    variant="outlined"
                    sx={{
                      border: isSelected ? 2 : 1,
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      position: 'relative',
                    }}
                  >
                    <CardContent>
                      {/* Header */}
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Box flex={1}>
                          <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                            <Typography variant="h6">
                              {quote.partnerName}
                            </Typography>
                            {quote.recommended && (
                              <Tooltip title="Recommended based on cost, quality, and lead time">
                                <StarIcon color="warning" fontSize="small" />
                              </Tooltip>
                            )}
                            <Chip
                              label={`Class ${quote.integrationClass}`}
                              size="small"
                              color={getIntegrationClassColor(quote.integrationClass)}
                            />
                          </Stack>

                          {/* Main Quote Info */}
                          <Grid container spacing={2} mb={1}>
                            <Grid item xs={4}>
                              <Typography variant="caption" color="text.secondary">
                                Total Cost/Unit
                              </Typography>
                              <Typography variant="h5" color="primary">
                                {formatCurrency(quote.quote.totalCost)}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="caption" color="text.secondary">
                                Lead Time
                              </Typography>
                              <Typography variant="body1">
                                {quote.quote.leadTimeDays} days
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="caption" color="text.secondary">
                                Score
                              </Typography>
                              <Typography variant="body1">
                                {quote.score?.toFixed(1) || 'N/A'}/100
                              </Typography>
                            </Grid>
                          </Grid>

                          {/* Expandable Details */}
                          <Collapse in={isExpanded}>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="subtitle2" mb={1}>
                              Cost Breakdown
                            </Typography>
                            <Grid container spacing={1} mb={2}>
                              {quote.quote.breakdown && (
                                <>
                                  {(quote.quote.breakdown.garmentCost > 0 || quote.quote.garmentCost > 0) && (
                                    <Grid item xs={6}>
                                      <Typography variant="caption" color="text.secondary">
                                        Garment
                                      </Typography>
                                      <Typography variant="body2">
                                        {formatCurrency(quote.quote.breakdown.garmentCost || quote.quote.garmentCost)}
                                      </Typography>
                                    </Grid>
                                  )}
                                  <Grid item xs={6}>
                                    <Typography variant="caption" color="text.secondary">
                                      Decoration
                                    </Typography>
                                    <Typography variant="body2">
                                      {formatCurrency(quote.quote.breakdown.decorationCost || quote.quote.decorationCost)}
                                    </Typography>
                                  </Grid>
                                  {quote.quote.breakdown.setupFee > 0 && (
                                    <Grid item xs={6}>
                                      <Typography variant="caption" color="text.secondary">
                                        Setup Fee
                                      </Typography>
                                      <Typography variant="body2">
                                        {formatCurrency(quote.quote.breakdown.setupFee)}
                                      </Typography>
                                    </Grid>
                                  )}
                                  {quote.quote.breakdown.shipping > 0 && (
                                    <Grid item xs={6}>
                                      <Typography variant="caption" color="text.secondary">
                                        Shipping (est.)
                                      </Typography>
                                      <Typography variant="body2">
                                        {formatCurrency(quote.quote.breakdown.shipping)}
                                      </Typography>
                                    </Grid>
                                  )}
                                </>
                              )}
                            </Grid>

                            {quote.quote.metadata && (
                              <>
                                <Typography variant="subtitle2" mb={1}>
                                  Additional Info
                                </Typography>
                                <Stack spacing={0.5}>
                                  {quote.quote.metadata.quoteId && (
                                    <Typography variant="caption" color="text.secondary">
                                      Quote ID: {quote.quote.metadata.quoteId}
                                    </Typography>
                                  )}
                                  {quote.quote.metadata.validUntil && (
                                    <Typography variant="caption" color="text.secondary">
                                      Valid until: {new Date(quote.quote.metadata.validUntil).toLocaleDateString()}
                                    </Typography>
                                  )}
                                </Stack>
                              </>
                            )}
                          </Collapse>

                          {/* Expand/Collapse Button */}
                          <Button
                            size="small"
                            onClick={() => toggleExpanded(quote.partnerId)}
                            endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            sx={{ mt: 1 }}
                          >
                            {isExpanded ? 'Less Details' : 'More Details'}
                          </Button>
                        </Box>

                        {/* Select Button */}
                        <Box ml={2}>
                          <Button
                            variant={isSelected ? 'contained' : 'outlined'}
                            color="primary"
                            onClick={() => handleSelectQuote(quote)}
                            startIcon={isSelected ? <CheckCircleIcon /> : null}
                            disabled={isSelected}
                          >
                            {isSelected ? 'Selected' : 'Select'}
                          </Button>
                        </Box>
                      </Stack>

                      {/* Timestamp */}
                      {quote.quote.quotedAt && (
                        <Typography variant="caption" color="text.secondary" mt={1} display="block">
                          Quoted {formatDistanceToNow(new Date(quote.quote.quotedAt), { addSuffix: true })}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          )}

          {quotes.length === 0 && !loading && (
            <Alert severity="info">
              Click "Get Quotes" to fetch production cost estimates from our partner network.
              Make sure garment details and decoration specifications are filled in.
            </Alert>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default PartnerQuoteWidget;
